import type { ComponentProps } from "react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/shared/components/forms/ControlledOTPInput/components/input-otp";
import { cn } from "@/shared/utils/style.util";

type ControlledOTPInputNewProps = {
  dataTestId: string
  maxLength: number
  groupLength: number
} & Omit<ComponentProps<typeof InputOTP>, "children" | "render">;

export const OTPInput = ({
  dataTestId,
  maxLength = 6,
  groupLength = 2,
  ...props
}: ControlledOTPInputNewProps) => {
  const inputLengthInGroup = maxLength / groupLength;
  const isLongLength = maxLength > 6;

  return (
    <InputOTP
      {...props}
      maxLength={maxLength}
      data-testid={dataTestId}
      containerClassName={cn(
        "flex gap-8 justify-center items-center flex-nowrap",
        isLongLength && "gap-2 sm:gap-8 short:!gap-2",
      )}
    >
      {Array.from({ length: groupLength }, (_, groupIndex) => (
        <InputOTPGroup
          key={groupIndex}
          className={cn(
            "flex gap-2",
            isLongLength && "gap-[4px] sm:gap-2 short:!gap-[4px]",
          )}
        >
          {Array.from({ length: inputLengthInGroup }, (_, index) => (
            <InputOTPSlot
              key={index}
              index={groupIndex * inputLengthInGroup + index}
              className={cn(
                "flex justify-center items-center w-10 h-[42px] text-sm font-extrabold text-center",
                // Flowbite TextInput styling
                "!rounded-lg !border !border-gray-300 dark:!border-gray-600",
                "bg-gray-50 dark:bg-gray-700",
                "text-gray-900 dark:text-white",
                "disabled:cursor-not-allowed disabled:opacity-50",
                // Override default shadcn connected styling
                "!shadow-none",
                // Override focus state (uses data-[active=true] instead of focus)
                "data-[active=true]:!border-primary dark:data-[active=true]:!border-primary",
                "data-[active=true]:!ring-1 data-[active=true]:!ring-primary",
                "data-[active=true]:!z-10",
                isLongLength && "w-8 sm:w-10 short:!w-8 short:sm:!h-10",
              )}
            />
          ))}
        </InputOTPGroup>
      ))}
    </InputOTP>
  );
};
